import React, { useEffect, useContext, useState, useRef, Suspense } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { AppContext } from "../utilities/context";
import { useCart } from "../context/CartContext";
import axios from "axios";
import Container from "@mui/material/Container";
import LayoutCustomer from "./LayoutCustomer";
import {
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Paper,
  TextField,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import QrCodeScanner from "@mui/icons-material/QrCodeScanner";
import List from "@mui/material/List";
import CheckoutDialog from "../components/CheckoutDialog";
import ProductModal from "../components/ProductModal";
import Styles from "../../public/assets/css/main.css";

const BarcodeScanner = React.lazy(() => import("../components/BarcodeScanner"));

const LocationEndPoint = () => {
  const intl = useIntl();
  const context = useContext(AppContext);
  const { hashId } = useParams();
  const {
    addToCart,
    cartCount,
    total,
    updateCartQuantity,
    handleProductNotesChange,
  } = useCart();
  const [location, setLocation] = useState(null);
  const [menu, setMenu] = useState([]);
  const [prevOrderIds, setPrevOrderIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCartDialog, setOpenCartDialog] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const textFieldRef = useRef(null);
  const productTextFieldRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [barcodeScannerOpen, setBarcodeScannerOpen] = useState(false);
  const [cameraDeviceId, setCameraDeviceId] = useState(null);

  useEffect(() => {
    getLocationData();
    if (context) {
      setTenantConfig(context.tenantConfig);
    }
  }, [hashId]);

  async function getLocationData() {
    setLoading(true);
    try {
      const response = await axios.get(`/api/qr/${hashId}`);
      if (response.data) {
        console.log("Location data:", response.data);
        setLocation(response.data.location);
        setMenu(response.data.menu);
        // BUG FIX: Asegurarse de que prevOrderIds tenga el shopId ya que me muestra pedidos de otras tiendas
        setPrevOrderIds(response.data.prevOrderIds || []);
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
      setLocation(null);
      setMenu([]);
      setSnackbar({
        open: true,
        message: intl.formatMessage({
          id: "app.error.locationNotFound",
          defaultMessage: "Ubicación no encontrada o error al cargar el menú.",
        }),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  const handleAddToCart = (product) => {
    addToCart(product);
    setSnackbar({
      open: true,
      message: intl.formatMessage(
        {
          id: "app.productAddedToCart",
          defaultMessage: `{productName} añadido al carrito.`,
        },
        { productName: product.name || product.product.name }
      ),
      severity: "success",
    });
  };

  const handleOpenCartDialog = () => {
    setOpenCartDialog(true);
  };

  const handleCloseCartDialog = () => {
    setOpenCartDialog(false);
  };

  const handleOpenScanner = async () => {
    setCameraDeviceId(null);
    setBarcodeScannerOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const track = stream.getVideoTracks()[0];
      const deviceId = track.getSettings().deviceId;
      stream.getTracks().forEach((t) => t.stop());
      setCameraDeviceId(deviceId);
    } catch (err) {
      console.error("Camera pre-permission error:", err);
    }
  };

  const handleProductModal = (bool, product) => {
    if (product) {
      product.productNotes = product.productNotes || "";
      product.quantity = product.quantity || 1;
    }
    setProductOpen(bool);
    setSelectedProduct(product);
  };

  const findProductByBarcode = (barcode) => {
    const flattenProducts = (categories) => {
      const result = [];
      categories.forEach((cat) => {
        if (cat.products) result.push(...cat.products);
        if (cat.subCategories)
          result.push(...flattenProducts(cat.subCategories));
      });
      return result;
    };
    const allProducts = flattenProducts(menu);
    return allProducts.find(
      (p) => p.barCode && p.barCode.toString() === barcode.toString()
    );
  };

  const handleBarcodeScan = (barcode) => {
    const product = findProductByBarcode(barcode);
    if (product) {
      handleProductModal(true, product);
      setSnackbar({
        open: true,
        message: `Producto encontrado: ${product.name}`,
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message: `No se encontró ningún producto con el código: ${barcode}`,
        severity: "warning",
      });
    }
  };

  const buildImageUrl = (url) => {
    if (!url) return null;
    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  if (loading) {
    return <div>Cargando ubicación...</div>;
  }

  if (!location) {
    return (
      <div>
        <Typography variant="h6" color="error">
          Ubicación no encontrada.
        </Typography>
      </div>
    );
  }

  const renderPrevOrderIds = (prevOrderIds) => (
    <div>
      {prevOrderIds.map((orderId) => (
        <Button
          variant="outlined"
          color="primary"
          key={orderId}
          style={{ marginRight: 8, marginBottom: 8 }}
          href={"/order-confirmation/" + orderId}
        >
          Pedido #{orderId}
        </Button>
      ))}
    </div>
  );

  const renderProducts = (products) => (
    <List sx={{ width: '100%', p: 0 }}>
      {products.map((product) => {
        const firstImage =
          product.images && product.images.length > 0
            ? buildImageUrl(product.images[0])
            : null;
        return (
          <Card
            variant="outlined"
            key={product.id}
            sx={{ mb: 1, border: "1px solid #e0e0e0", mx: { xs: -1, sm: 0 } }}
          >
            <CardContent onClick={() => handleProductModal(true, product)} sx={{cursor:"pointer"}}>
              <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
                <Grid item xs={firstImage ? 8 : 12}>
                  <Typography variant="h6">{product.name}</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                    {product.description}
                  </Typography>
                  {product.tags && product.tags.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Tags: {product.tags.map((tag) => tag.name).join(", ")}
                    </Typography>
                  )}
                  <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                    $
                    {" " +
                      new Intl.NumberFormat("es-AR", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(Number(product.price) || 0)}
                  </Typography>
                </Grid>

                {firstImage ? (
                  <Grid item xs={4} sx={{ alignSelf: "flex-start" }}>
                    <Box
                      sx={{
                        width: "100%",
                        height: "150px",
                        overflow: "hidden",
                        borderRadius: 1,
                        border: "1px solid #eee",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={firstImage}
                        alt={product.name}
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </CardContent>
          </Card>
        );
      })}
    </List>
  );

  const renderMenuCategory = (category) => (
    <Box key={category.id} sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{ borderBottom: "1px solid #ccc", pb: 1, mt: 2 }}
      >
        {category.name}
      </Typography>
      {category.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {category.description}
        </Typography>
      )}

      {category.products &&
        category.products.length > 0 &&
        renderProducts(category.products)}

      {category.subCategories && category.subCategories.length > 0 && (
        <Box sx={{ pl: 2, mt: 2 }}>
          {category.subCategories.map((subCat) => renderMenuCategory(subCat))}
        </Box>
      )}

      {(!category.products || category.products.length === 0) &&
        (!category.subCategories || category.subCategories.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <FormattedMessage
              id="app.noItemsInCategory"
              defaultMessage="No hay productos en esta categoría."
            />
          </Typography>
        )}
    </Box>
  );

  const handleSelectedProductNotesChange = (notes) => {
    const updatedProduct = {
      ...selectedProduct,
      productNotes: notes,
    };
    setSelectedProduct(updatedProduct);
  };

  const handleSelectedProductQuantityChange = (quantity) => {
    const updatedProduct = {
      ...selectedProduct,
      quantity: quantity,
    };
    setSelectedProduct(updatedProduct);
  };

  return (
    <LayoutCustomer location={location}>
      <Container maxWidth="md" sx={{ mt: 2, mb: 4, px: { xs: 1, sm: 3 } }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography variant="h4" component="h1" gutterBottom>
              {location.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              <FormattedMessage
                id={"app." + location.shop.type + "Welcome"}
                defaultMessage="Bienvenido a nuestra carta."
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {renderPrevOrderIds(prevOrderIds)}
          </Grid>

          {location.shop.type === "QUICK_CHECKOUT" && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<QrCodeScanner />}
              onClick={handleOpenScanner}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Escanear código de barras
            </Button>
          </Grid>
          )}

          <Grid item xs={12}>
            {menu.length > 0 ? (
              menu.map((category) => renderMenuCategory(category))
            ) : (
              <Typography variant="body1">
                <FormattedMessage
                  id="app.noMenuAvailable"
                  defaultMessage="No hay menú disponible para esta ubicación."
                />
              </Typography>
            )}
          </Grid>
        </Grid>

        {cartCount > 0 && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ShoppingCartIcon />}
            onClick={handleOpenCartDialog}
            sx={{
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000,
              padding: "12px 24px",
              fontSize: "1rem",
              borderRadius: "24px",
            }}
          >
            <FormattedMessage
              id="app.viewCart"
              defaultMessage="Ver Carrito ({itemCount})"
              values={{ itemCount: cartCount }}
            />{" "}
            - ${total}
          </Button>
        )}

        <CheckoutDialog
          open={openCartDialog}
          onClose={handleCloseCartDialog}
          locationHashId={hashId}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <ProductModal
          open={productOpen}
          selectedProduct={selectedProduct}
          onClose={() => handleProductModal(false, null)}
          onNotesChange={handleSelectedProductNotesChange}
          onQuantityChange={handleSelectedProductQuantityChange}
          onAddToCart={handleAddToCart}
          buildImageUrl={buildImageUrl}
          fullScreen={fullScreen}
          showCartButton={location.shop.additionalInfo.features.cart || false}
        />

        <BarcodeScanner
          open={barcodeScannerOpen}
          onClose={() => setBarcodeScannerOpen(false)}
          onScan={handleBarcodeScan}
          deviceId={cameraDeviceId}
        />
      </Container>
    </LayoutCustomer>
  );
};

export default LocationEndPoint;
